import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { isToday, parseISO } from 'date-fns';
import { Info, TriangleAlert, Unlink } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Textarea } from '@/components/ui/textarea';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { useFieldArrayKeyboard } from '@/hooks/use-field-array-keyboard';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { TimePicker } from '@/modules/common/components/molecules/date/time-picker';
import NumberInput from '@/modules/common/components/molecules/inputs/number-input';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import {
  getIntegrationDisplayName,
  getIntegrationIcon,
} from '@/modules/common/utils/integration-icons';
import { TimeEntryModalData } from '@/modules/time-tracking/types/time-tracking';

import { getTimeTrackDefaultValues } from '../../helpers';
import { computeElapsedSeconds } from '../../helpers/compute-elapsed.helper';
import {
  convertTimeEntryToTimeTrack,
  convertTimeTrackToTimeEntry,
  getDurationObject,
  getTime,
} from '../../helpers/format-time-track.helper';
import { useDurationOverflow } from '../../hooks/use-duration-overflow';
import { useViewModeAndDate } from '../../hooks/use-view-mode-and-date';
import { timeEntryFormSchema, TimeEntryFormValues } from '../../schema';
import { ActiveTimerResponse } from '../../types/dto/active-timer.dto';
import { CreateWorklogRequest } from '../../types/dto/worklogs-summary.dto';
import { IntegrationDisconnectConfirmDialog } from './integration-disconnect-confirm-dialog';
import { TimeTrackerProjectSelect } from './time-tracker-project-select';

/**
 * Matches the Dialog's `duration-200` close animation (see `dialog.tsx`). We
 * defer clearing the form until the exit finishes so the closing modal doesn't
 * flash default values (OPS-1551). A little headroom avoids clearing mid-fade.
 */
const DIALOG_EXIT_ANIMATION_MS = 250;

/** Once the user confirms editing an integration-linked entry, never ask again on this device. */
const INTEGRATION_DISCONNECT_CONFIRM_DISMISSED_KEY =
  'worklog_integration_disconnect_confirm_dismissed';
interface TimeEntryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateWorklogRequest) => void;
  timeTrack?: TimeEntryModalData;
  defaultProjectId?: string;
  defaultProject?: DropdownSearchItem | null;
  defaultDescription?: string;
  /**
   * Pre-filled date for new entries. Overrides the route's `selectedDate` —
   * used by the week-view header quick-add so clicking on Wednesday opens the
   * modal with Wednesday selected regardless of the calendar's current anchor.
   */
  initialDate?: Date;
  isRunning?: boolean;
  isTimerSetup?: boolean;
  onSave?: (values: CreateWorklogRequest, project: DropdownSearchItem | null) => void;
  activeTimer?: ActiveTimerResponse;
  activeTimerFetchedAt?: number;
}

export function TimeEntryModal({
  open,
  onClose,
  onSubmit,
  timeTrack,
  defaultProjectId,
  defaultProject,
  defaultDescription,
  initialDate,
  isRunning,
  isTimerSetup,
  onSave,
  activeTimer,
  activeTimerFetchedAt,
}: TimeEntryModalProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const [loading, setLoading] = useState(false);
  const { selectedDate } = useViewModeAndDate();
  const [selectedProject, setSelectedProject] = useState<DropdownSearchItem | null>(null);

  // For running/setup timer modes, always use today — selectedDate reflects the
  // time-tracking page's calendar selection and can be stale (e.g. opened yesterday).
  // When `initialDate` is provided, callers want an explicit pre-filled date
  // independent of the route's selected date (e.g. week-view header quick-add).
  // Also used to center the project picker's booking-check window (see
  // TimeTrackerProjectSelect).
  const baseDate = useMemo(
    () => (isRunning || isTimerSetup ? new Date() : (initialDate ?? selectedDate)),
    // `open` forces a fresh `new Date()` each time the modal reopens in running/setup
    // mode, instead of reusing a stale timestamp from whenever these flags last changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRunning, isTimerSetup, initialDate, selectedDate, open]
  );

  // Prepare default values
  const defaultValues: TimeEntryFormValues = useMemo(() => {
    if (timeTrack) {
      const timeEntry = convertTimeTrackToTimeEntry(timeTrack);
      return {
        projectId: String(timeEntry.projectId),
        date: timeEntry.date instanceof Date ? timeEntry.date : parseISO(timeEntry.date),
        startTime: timeEntry.startTime,
        duration: {
          hours: timeEntry.duration.hours,
          minutes: timeEntry.duration.minutes,
        },
        description: timeEntry.description || '',
      };
    }
    const base = getTimeTrackDefaultValues(baseDate, defaultProject?.id ?? defaultProjectId);
    return { ...base, description: defaultDescription ?? '' };
  }, [timeTrack, baseDate, defaultProjectId, defaultProject, defaultDescription]);

  const form = useForm<TimeEntryFormValues>({
    resolver: zodResolver(timeEntryFormSchema(t)),
    mode: 'onChange',
  });

  const { reset, watch } = form;
  const { durationOverflow, setDurationOverflow, computeAndSet } = useDurationOverflow(form);

  // Latest default values, read by the seed effect WITHOUT depending on their
  // object identity. Parents pass unstable props (e.g. `defaultProject` is a
  // fresh object literal every render in project-card), so depending on
  // `defaultValues` directly would re-seed — and wipe the user's input — on any
  // re-render that happens while the modal is open (notably the query refetch
  // after a successful submit, which produced the OPS-1551 flash of old values).
  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;
  const defaultProjectRef = useRef(defaultProject);
  defaultProjectRef.current = defaultProject;

  // Seed the form ONLY when the modal opens or when the edited entry changes —
  // not on every prop-identity churn while open. `timeTrack?.id` covers
  // switching between entries (edit) and add↔edit transitions.
  const editingId = timeTrack?.id;
  useEffect(() => {
    if (!open) return;
    const values = defaultValuesRef.current;
    reset(values);
    if (defaultProjectRef.current) {
      setSelectedProject(defaultProjectRef.current);
    }
    computeAndSet(values.startTime, values.date, values.duration?.hours, values.duration?.minutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  // Clear the form only AFTER the dialog's exit animation finishes, so the
  // closing modal keeps showing the values the user just submitted instead of
  // flashing defaults (OPS-1551). The open effect above re-seeds on next open.
  useEffect(() => {
    if (open) return;
    const timeoutId = window.setTimeout(() => {
      reset();
      setDurationOverflow(null);
      setSelectedProject(null);
    }, DIALOG_EXIT_ANIMATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open, reset, setDurationOverflow]);

  const date = watch('date');

  // Calculate max time for running entries (current time) only if date is today
  const maxTimeForPicker = useMemo(() => {
    if (!isRunning || !date || !isToday(date)) return undefined;
    return getTime(new Date());
  }, [isRunning, date]);

  useEffect(() => {
    if (!isRunning || !activeTimer || activeTimerFetchedAt === undefined) return;
    // Use accumulatedSeconds + live delta from fetchedAt — timezone-independent
    const updateDuration = () => {
      const elapsedSeconds = computeElapsedSeconds(activeTimer, activeTimerFetchedAt);
      form.setValue('duration', getDurationObject(elapsedSeconds / 3600));
    };

    updateDuration();
    const intervalId = setInterval(updateDuration, 1000);
    return () => clearInterval(intervalId);
  }, [isRunning, activeTimer, activeTimerFetchedAt, form]);

  const isEditMode = !!timeTrack?.id;
  // Already the mapped domain status — the DTO → domain conversion happens once,
  // where the backend response is turned into a `TimeTrack` (see `mapTimeTrackFromResponse`).
  const integrationStatus = timeTrack?.integrations;
  // Only an active (still-connected) link means editing here would disconnect it — a
  // soft-deleted link is already disconnected, so it shouldn't trigger the warning/confirm.
  const isLinkedToIntegration = !!integrationStatus?.isConnected;
  const integrationName = integrationStatus
    ? getIntegrationDisplayName(integrationStatus.service)
    : '';

  // Rendered next to the title: the provider's own icon while connected, an unlink icon once
  // disconnected. The tooltip carries the explanation that used to sit in an in-body callout.
  const integrationBadge = useMemo(() => {
    if (!integrationStatus) return null;
    if (integrationStatus.isConnected) {
      return {
        tooltip: t('modal.addEntry.fields.integrationLinkedWarning', {
          integration: integrationName,
        }),
        icon: (
          <img
            src={getIntegrationIcon(integrationStatus.service)}
            alt={integrationName}
            className="h-5 w-5 shrink-0"
          />
        ),
      };
    }
    return {
      tooltip: t('modal.addEntry.fields.integrationUnlinkedNotice', {
        integration: integrationName,
      }),
      // Same warning tone the Alert component uses, so it reads as "needs your attention" in both themes.
      icon: <Unlink className="h-5 w-5 shrink-0 text-amber-600 dark:text-orange-400" />,
    };
  }, [integrationStatus, integrationName, t]);

  const [hasDismissedIntegrationConfirm, setHasDismissedIntegrationConfirm] = useLocalStorage(
    INTEGRATION_DISCONNECT_CONFIRM_DISMISSED_KEY,
    false
  );
  const [isIntegrationConfirmOpen, setIsIntegrationConfirmOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<TimeEntryFormValues | null>(null);

  const duration = watch('duration');
  const hasZeroDuration =
    (duration?.hours === 0 || duration?.hours === undefined) &&
    (duration?.minutes === 0 || duration?.minutes === undefined);

  const isProjectMissing = !watch('projectId');
  const submitBlockReasons = [
    ...(isProjectMissing ? [t('modal.addEntry.fields.noProjectTooltip')] : []),
    ...(hasZeroDuration ? [t('modal.addEntry.fields.zeroDurationTooltip')] : []),
  ];
  const submitBlockedTooltip =
    submitBlockReasons.length > 1 ? (
      <ul className="list-disc pl-4">
        {submitBlockReasons.map(reason => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    ) : submitBlockReasons.length === 1 ? (
      submitBlockReasons[0]
    ) : null;
  const isSubmitBlocked = submitBlockReasons.length > 0;

  const description = watch('description');
  const isDescriptionEmpty = !description || description.trim().length === 0;
  const descriptionWarning = isDescriptionEmpty
    ? t('modal.addEntry.fields.descriptionEmptyWarning')
    : undefined;

  // When the timer is running or in setup mode, duration/date fields are disabled or hidden —
  // skip their validation so save buttons aren't blocked by fields the user can't interact with.
  const { errors } = form.formState;
  const isFormValid =
    isRunning || isTimerSetup ? !errors.projectId && !errors.startTime : form.formState.isValid;

  const { ref: scrollRef, isScrollable } = useScrollOverflow([open]);

  const handleClose = () => {
    // Only ask the parent to close. The form is reset by the `open === false`
    // effect below — resetting synchronously here repaints the still-mounted
    // form with default/old values during the dialog's exit animation, which
    // looks like a flash of stale data before it disappears (OPS-1551).
    onClose();
  };

  const handleStopAndSave = async () => {
    const values = watch();
    try {
      setLoading(true);
      const submitValues = hasZeroDuration
        ? { ...values, duration: { hours: 0, minutes: 1 } }
        : values;
      await onSubmit(convertTimeEntryToTimeTrack(submitValues, selectedProject));
      handleClose();
    } catch (error) {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:modal.addEntry.error'
      );
    } finally {
      setLoading(false);
    }
  };

  const performSubmit = async (values: TimeEntryFormValues) => {
    try {
      setLoading(true);
      await onSubmit(convertTimeEntryToTimeTrack(values, selectedProject));
      if (!isRunning) {
        displayToastSuccess(
          t(isEditMode ? 'modal.addEntry.successEdit' : 'modal.addEntry.successAdd')
        );
      }
      handleClose();
    } catch (error) {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:modal.addEntry.error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: TimeEntryFormValues) => {
    // Editing an integration-linked entry disconnects it — confirm once, then remember the choice.
    if (isEditMode && isLinkedToIntegration && !hasDismissedIntegrationConfirm) {
      setPendingSubmitValues(values);
      setIsIntegrationConfirmOpen(true);
      return;
    }
    await performSubmit(values);
  };

  const handleIntegrationDisconnectConfirm = async () => {
    setHasDismissedIntegrationConfirm(true);
    setIsIntegrationConfirmOpen(false);
    if (pendingSubmitValues) {
      await performSubmit(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
  };

  const handleIntegrationDisconnectCancel = () => {
    setIsIntegrationConfirmOpen(false);
    setPendingSubmitValues(null);
  };

  const handleSave = () => {
    onSave?.(convertTimeEntryToTimeTrack(watch(), selectedProject), selectedProject);
    handleClose();
  };

  const handleAdjustStartTime = () => {
    const dur = watch('duration');
    const totalDurationMinutes = (dur?.hours ?? 0) * 60 + (dur?.minutes ?? 0);
    const newStartMinutes = 24 * 60 - totalDurationMinutes;
    const newHours = Math.floor(newStartMinutes / 60);
    const newMins = newStartMinutes % 60;
    const newStartTime = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
    form.setValue('startTime', newStartTime, { shouldValidate: true });
  };

  const { formRef, handleKeyDown } = useFieldArrayKeyboard({
    onSubmit: () => form.handleSubmit(handleSubmit)(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-0">
          <Form {...form}>
            <form
              ref={formRef}
              onSubmit={form.handleSubmit(handleSubmit)}
              onKeyDown={handleKeyDown}
              className="flex min-h-0 flex-1 flex-col"
            >
              <DialogHeader className={cn('shrink-0 pb-4', isScrollable && 'border-b')}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col space-y-1.5">
                    <DialogTitle>
                      {isTimerSetup
                        ? t('modal.addEntry.timerSetupTitle')
                        : isRunning
                          ? t('modal.addEntry.runningTitle')
                          : isEditMode
                            ? t('modal.addEntry.editTitle')
                            : t('modal.addEntry.title')}
                    </DialogTitle>
                    <p className="text-sm font-normal text-muted-foreground">
                      {t('modal.addEntry.subtitle')}
                    </p>
                  </div>
                  {integrationBadge && (
                    // `mr-6` keeps the badge clear of the dialog's absolutely-positioned close button.
                    <SimpleTooltip content={integrationBadge.tooltip}>
                      <span className="mr-6 flex shrink-0 items-center">
                        {integrationBadge.icon}
                      </span>
                    </SimpleTooltip>
                  )}
                </div>
              </DialogHeader>

              <div ref={scrollRef} className="space-y-4 overflow-y-auto py-4 pl-1 pr-3">
                {/* Project Select */}
                <TimeTrackerProjectSelect
                  name="projectId"
                  control={form.control}
                  setValue={form.setValue}
                  label={`${t('modal.addEntry.fields.project')} *`}
                  placeholder={t('modal.addEntry.fields.projectPlaceholder')}
                  onSelect={setSelectedProject}
                  initialSelectedItem={defaultProject ?? undefined}
                  anchorDate={baseDate}
                />

                {/* Date Picker */}
                {!isTimerSetup && (
                  <DatePicker
                    name="date"
                    control={form.control}
                    label={t('modal.addEntry.fields.date')}
                    dateFormat="D MMM, YYYY"
                    inputDisabled={isRunning}
                    required
                  />
                )}

                {/* Start Time */}
                {!isTimerSetup && (
                  <TimePicker
                    name="startTime"
                    control={form.control}
                    label={t('modal.addEntry.fields.startTime')}
                    hideSubText
                    maxTime={maxTimeForPicker}
                  />
                )}

                {/* Duration */}
                {!isTimerSetup && (
                  <div className="space-y-2">
                    <FormLabel>{t('modal.addEntry.fields.duration')}</FormLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <NumberInput
                        name="duration.hours"
                        control={form.control}
                        min={0}
                        max={23}
                        step={1}
                        suffixText={t('modal.addEntry.fields.durationHours')}
                        disabled={isRunning}
                      />

                      <NumberInput
                        name="duration.minutes"
                        control={form.control}
                        min={0}
                        max={59}
                        step={1}
                        suffixText={t('modal.addEntry.fields.durationMinutes')}
                        disabled={isRunning}
                      />
                    </div>
                    {durationOverflow && (
                      <Alert variant="warning">
                        <TriangleAlert className="h-4 w-4" />
                        <AlertDescription>
                          <span>
                            {t('modal.addEntry.fields.durationOverflowWarning', {
                              date: durationOverflow.date,
                              currentDateHours: durationOverflow.currentDateHours,
                              currentDateMinutes: durationOverflow.currentDateMinutes,
                              overflowHours: durationOverflow.overflowHours,
                              overflowMinutes: durationOverflow.overflowMinutes,
                            })}
                          </span>
                          <button
                            type="button"
                            onClick={handleAdjustStartTime}
                            className="mt-1 block font-medium underline underline-offset-2"
                          >
                            {t('modal.addEntry.fields.adjustStartTime')}
                          </button>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Description (Optional) */}
                <FormField
                  control={form.control}
                  name="description"
                  warning={descriptionWarning}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modal.addEntry.fields.description')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t('modal.addEntry.fields.descriptionPlaceholder')}
                          rows={5}
                          hasWarning={isDescriptionEmpty}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter
                className={cn(
                  'mt-4 shrink-0 justify-between sm:justify-between',
                  isScrollable && 'border-t pt-4'
                )}
              >
                <Button type="button" variant="outline" onClick={handleClose}>
                  {t('common:cancel')}
                </Button>
                <div className="flex items-center gap-2">
                  {isTimerSetup ? (
                    <Button
                      type="button"
                      disabled={!watch('projectId') || !isFormValid || loading}
                      onClick={handleSave}
                    >
                      {t('common:save')}
                    </Button>
                  ) : isRunning ? (
                    <>
                      <Button
                        variant="outline"
                        disabled={!watch('projectId') || !isFormValid || loading}
                        onClick={handleSave}
                      >
                        {t('common:save')}
                      </Button>
                      <ButtonLoadable
                        type="button"
                        isLoading={loading}
                        disabled={!watch('projectId') || !isFormValid || loading}
                        onClick={handleStopAndSave}
                      >
                        {t('modal.addEntry.actions.stopAndSave')}
                      </ButtonLoadable>
                    </>
                  ) : (
                    <SimpleTooltip content={submitBlockedTooltip}>
                      <span>
                        <ButtonLoadable
                          type="submit"
                          isLoading={loading}
                          disabled={!isFormValid || loading || isSubmitBlocked}
                          icon={isSubmitBlocked ? <Info className="h-4 w-4" /> : undefined}
                        >
                          {isEditMode ? t('common:update') : t('modal.addEntry.actions.addEntry')}
                        </ButtonLoadable>
                      </span>
                    </SimpleTooltip>
                  )}
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <IntegrationDisconnectConfirmDialog
        open={isIntegrationConfirmOpen}
        integrationName={integrationName}
        isLoading={loading}
        onConfirm={handleIntegrationDisconnectConfirm}
        onCancel={handleIntegrationDisconnectCancel}
      />
    </>
  );
}
